import { useState, type FormEvent } from "react";
import {
  ArrowRight,
  Eye,
  EyeOff,
  KeyRound,
  LockKeyhole,
  ShieldCheck,
} from "lucide-react";
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@weblinkpong/ui";
import { loginAdmin, type AdminUser } from "./api";
import { useAdminToasts } from "./hooks/use-admin-toasts";

export function AdminLogin({
  onSuccess,
}: {
  onSuccess: (admin: AdminUser) => void;
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  useAdminToasts({ error }, "admin-login");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError("");
    const data = new FormData(event.currentTarget);
    try {
      onSuccess(
        await loginAdmin(
          String(data.get("email")),
          String(data.get("password")),
        ),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Sign in failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="grid min-h-svh lg:grid-cols-[1.1fr_.9fr]">
      <section className="relative hidden overflow-hidden bg-slate-950 p-10 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(59,130,246,.35),transparent_32%),radial-gradient(circle_at_80%_80%,rgba(37,99,235,.22),transparent_35%)]" />
        <div className="relative flex items-center gap-3">
          <span className="grid size-10 place-items-center rounded-xl bg-blue-600 text-lg font-bold">
            W
          </span>
          <div>
            <strong className="block text-base">WebLinkPong</strong>
            <small className="text-sm text-slate-300">Operations console</small>
          </div>
        </div>
        <div className="relative max-w-xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium">
            <ShieldCheck /> Owner-controlled access
          </span>
          <h1 className="mt-5 text-4xl font-bold tracking-tight xl:text-5xl">
            One calm place to run every website order.
          </h1>
          <p className="mt-4 text-base leading-7 text-slate-300">
            Payments, projects, support, renewals and platform health—protected
            by staff roles and a complete audit trail.
          </p>
        </div>
        <div className="relative flex flex-wrap gap-3 text-xs text-slate-300">
          <span className="inline-flex items-center gap-2">
            <LockKeyhole /> HttpOnly staff session
          </span>
          <span className="inline-flex items-center gap-2">
            <KeyRound /> CSRF-protected mutations
          </span>
          <span className="inline-flex items-center gap-2">
            <ShieldCheck /> Server-side permission checks
          </span>
        </div>
      </section>
      <section className="flex items-center justify-center bg-background p-6 sm:p-10">
        <Card className="w-full max-w-md">
          <CardHeader className="px-6 pt-6 sm:px-8 sm:pt-8">
            <span className="text-xs font-medium uppercase tracking-wider text-primary">
              Staff access
            </span>
            <CardTitle className="text-2xl">Sign in to WebLinkPong</CardTitle>
            <CardDescription>
              Use the owner or staff account assigned from this deployment.
            </CardDescription>
          </CardHeader>
          <CardContent className="px-6 pb-6 sm:px-8 sm:pb-8">
            <form className="grid gap-5" onSubmit={submit}>
              <div className="grid gap-2">
                <Label htmlFor="admin-email">Email address</Label>
                <Input
                  id="admin-email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  placeholder="owner@company.com"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="admin-password">Password</Label>
                <div className="relative">
                  <Input
                    id="admin-password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    minLength={12}
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute top-1/2 right-1 -translate-y-1/2"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    onClick={() => setShowPassword((value) => !value)}
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </Button>
                </div>
              </div>
              {error ? (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}
              <Button className="w-full" type="submit" disabled={loading}>
                {loading ? "Signing in…" : "Sign in securely"}
                <ArrowRight />
              </Button>
              <p className="text-center text-xs leading-5 text-muted-foreground">
                First deployment? Configure the bootstrap owner in deployment
                secrets, then sign in once to create the audited owner account.
              </p>
            </form>
          </CardContent>
        </Card>
      </section>
    </main>
  );
}
